import {
	CustomerOrderOfferLinesDataGridApi,
	CustomerOrderOfferLinesDataGridApi_CustomerOrderOfferLineDto,
	CustomerOrderOfferLinesDataGridApi_Data,
} from "src/api/generated/erp/sales/customerOrderOffer/api/customerOrderOfferLinesDataGridApi";
import {
	ClientSideDataGridModel,
	ClientSideDataGridModelRenderProps,
} from "src/components/common/dataGrid/gridModel/ClientSideDataGridModel";
import { FormCrudDataGrid, FormCrudDataGridModelProps } from "src/components/common/dataGrid/crud/FormCrudDataGrid";
import {
	booleanColumn,
	dateColumn,
	enumColumn,
	floatColumn,
	integerColumn,
	moneyColumn,
	textColumn,
} from "src/components/common/dataGrid/columns.tsx";
import i18n from "i18next";
import { getSalesPartTypeLabels } from "src/api/generated/erp/db/types/enums/salesPartType.ts";
import { DocumentsOfObjectButton } from "src/components/views/documents/objectDocuments/DocumentsOfObjectButton.tsx";
import { nullableAavoObjectRef } from "src/utils/aavoObjectRefUtils.ts";
import { AavoTextField } from "src/components/common/inputFields/AavoTextField.tsx";
import { CustomerOrderOrOfferSummaryText } from "src/components/views/erp/sales/customerOrderLine/utils/CustomerOrderOrOfferSummaryText.tsx";
import { faTrash } from "@fortawesome/pro-regular-svg-icons";
import { useConfirmDialog } from "src/components/common/dialogs/confirmDialog/ConfirmDialogContext.ts";
import { CustomerOrderOfferLinesDataGridRowContextMenu } from "./CustomerOrderOfferLinesDataGridRowContextMenu";
import { CustomerOrderOfferLineForm } from "src/components/views/erp/sales/customerOrderOffer/CustomerOrderOfferLineForm.tsx";
import { CustomerOrderOfferLineEditApi } from "src/api/generated/erp/sales/customerOrderOffer/api/customerOrderOfferLineEditApi.ts";
import { Permissions } from "src/api/generated/iam/permissions.ts";
import { useHasPermission } from "src/components/views/erp/common/userPermissions.ts";
import { RefreshableElementRef } from "src/utils/useRefreshRef.ts";
import { useTenantCustomizations } from "src/tenantCustomizations/TenantCustomizationsContext.ts";
import { ImportCustomerOfferLinesFromTabularDataButton } from "./ImportCustomerOfferLinesFromTabularDataButton.tsx";
import { useState } from "react";
import { GridRowParams } from "@mui/x-data-grid/models";
import { HorizontalBox } from "src/components/common/box/HorizontalBox";
import { StaticDataGridModel } from "src/components/common/dataGrid/gridModel/StaticDataGridModel.tsx";
import { getOnlyMember } from "src/utils/arrayUtils.ts";
import { aavoGridDetailPanelToggleCol } from "src/components/common/dataGrid/masterDetail/aavoGridDetailPanelToggleCol.tsx";
import { AsyncButton } from "src/components/common/buttons/AsyncButton.tsx";
import { ImportFromVertexIndButton } from "src/components/views/erp/integrations/vertexInd/ImportFromVertexIndButton.tsx";
import { VertexToCustomerOfferLineApi } from "src/api/generated/erp/sales/customerOrderOffer/importing/vertexToCustomerOfferLineApi.ts";
import { canEditCustomerOrderOffer } from "./customerOrderOfferStateGuards.ts";
import { formatLabelWithCurrencyCode, renderMoneyCellWithCurrency } from "src/components/views/erp/sales/salesUtils.ts";
import { defineAgentUiContextKey } from "src/components/views/ai/assistant/uiContext/agentUiContextKey.ts";

export interface CustomerOrderOfferLinesDataGridProps {
	customerOrderOfferId: number;
	parentLineId: number | null;
	hideActionBar?: boolean;
	hideSummary?: boolean;
	refreshRef?: RefreshableElementRef;
	editable?: boolean;
}

export const CustomerOrderOfferLinesDataGrid = (props: CustomerOrderOfferLinesDataGridProps) => {
	const { customerOrderOfferId, parentLineId, refreshRef } = props;

	// Represents all selected rows, regardless if they are in master or detail data grid.
	const [globalSelectedRows, setGlobalSelectedRows] = useState<
		CustomerOrderOfferLinesDataGridApi_CustomerOrderOfferLineDto[]
	>([]);
	const localSelectedRows = globalSelectedRows.filter((row) => row.parentLineId == parentLineId);

	return (
		<ClientSideDataGridModel
			fetchData={({ searchQuery }) =>
				CustomerOrderOfferLinesDataGridApi.getData({
					customerOrderOfferId: customerOrderOfferId,
					parentLineId: parentLineId,
					searchQuery: searchQuery,
				})
			}
			initialParams={{
				searchQuery: "",
			}}
			refreshRef={refreshRef}
			getRows={(data) => data.customerOrderOfferLines}
			getRowId={(row) => row.customerOrderOfferLineId}
			customFunctionUiActionView={"CUSTOMER_OFFER_LINES"}
			gridId={"D74A0E43F5A304F8"}
			selectedRows={localSelectedRows}
			onSelectionChanged={setGlobalSelectedRows}
			render={(params) => (
				<CustomerOrderOfferLinesDataGridInner
					{...props}
					{...params}
					globalSelectedRows={globalSelectedRows}
					setGlobalSelectedRows={setGlobalSelectedRows}
				/>
			)}
		/>
	);
};

interface DataGridParams {
	searchQuery: string;
}

interface CustomerOrderOfferLinesDataGridInnerProps
	extends
		CustomerOrderOfferLinesDataGridProps,
		Omit<
			ClientSideDataGridModelRenderProps<
				CustomerOrderOfferLinesDataGridApi_Data,
				CustomerOrderOfferLinesDataGridApi_CustomerOrderOfferLineDto,
				DataGridParams
			>,
			"dataGridProps"
		> {
	dataGridProps: FormCrudDataGridModelProps<CustomerOrderOfferLinesDataGridApi_CustomerOrderOfferLineDto>;
	globalSelectedRows: CustomerOrderOfferLinesDataGridApi_CustomerOrderOfferLineDto[];
	setGlobalSelectedRows: (rows: CustomerOrderOfferLinesDataGridApi_CustomerOrderOfferLineDto[]) => void;
}

const CustomerOrderOfferLinesDataGridInner = (props: CustomerOrderOfferLinesDataGridInnerProps) => {
	const {
		customerOrderOfferId,
		parentLineId,
		dataGridProps,
		refreshData,
		data,
		hideActionBar,
		hideSummary,
		globalSelectedRows,
	} = props;

	const { customerOrderOffer, offerSummary } = data;
	const sellingCurrencyCode = offerSummary.currencyCode;
	const domesticCurrencyCode = offerSummary.domesticCurrencyCode;

	const { tenantConfig } = useTenantCustomizations();
	const showConfirmDialog = useConfirmDialog();

	const editEnabled =
		props.editable !== false && canEditCustomerOrderOffer(customerOrderOffer.customerOrderOfferState);
	const hasPermission = useHasPermission();
	const showProductionFields = hasPermission(Permissions.Sales.CustomerOffers.READ_ALL);
	const canReadCostData = hasPermission(Permissions.Sales.CustomerOffers.READ_COSTS);

	const globalOnlySelectedRow = getOnlyMember(globalSelectedRows);
	const deleteDisabledReason = getDeleteDisabledReason();

	return (
		<FormCrudDataGrid<CustomerOrderOfferLinesDataGridApi_CustomerOrderOfferLineDto>
			listViewOnMobile
			hideActionBar={hideActionBar}
			columns={[
				aavoGridDetailPanelToggleCol<CustomerOrderOfferLinesDataGridApi_CustomerOrderOfferLineDto>(
					(row) => row.childLines.length > 0,
				),
				integerColumn({
					field: "lineNumber",
					headerName: i18n.t("number_shortened"),
					width: 50,
					mobile: "cell",
				}),
				textColumn({
					field: "configurationOrPartDescription",
					headerName: i18n.t("part"),
					width: 250,
					mobile: "header",
				}),
				textColumn({
					field: "currencyCode",
					headerName: i18n.t("currency"),
					width: 100,
				}),
				floatColumn({
					field: "salesQuantityAsSalesUnits",
					headerName: i18n.t("ordered_quantity"),
					mobile: "cell",
				}),
				textColumn({
					field: "salesUnit",
					headerName: i18n.t("sales_unit"),
				}),
				textColumn({
					field: "mark",
					headerName: i18n.t("mark"),
					width: 200,
					mobile: "cell",
				}),
				parentLineId == null && [
					dateColumn({
						field: "plannedDeliveryDate",
						headerName: i18n.t("planned_delivery_date"),
						width: 160,
					}),
					moneyColumn({
						field: "unitPrice",
						headerName: formatLabelWithCurrencyCode(i18n.t("unit_price"), sellingCurrencyCode),
						width: 90,
						renderCell: renderMoneyCellWithCurrency(sellingCurrencyCode),
					}),
					moneyColumn({
						field: "unitPriceWithVat",
						headerName: formatLabelWithCurrencyCode(i18n.t("unit_price_with_vat"), sellingCurrencyCode),
						width: 150,
						renderCell: renderMoneyCellWithCurrency(sellingCurrencyCode),
					}),
					floatColumn({
						field: "discountPercentage",
						headerName: i18n.t("discount_percentage"),
						width: 80,
					}),
					moneyColumn({
						field: "totalPrice",
						headerName: formatLabelWithCurrencyCode(i18n.t("total_price"), sellingCurrencyCode),
						renderCell: renderMoneyCellWithCurrency(sellingCurrencyCode),
						mobile: "cell",
					}),
					moneyColumn({
						field: "totalPriceWithVat",
						headerName: formatLabelWithCurrencyCode(i18n.t("total_price_with_vat"), sellingCurrencyCode),
						width: 155,
						renderCell: renderMoneyCellWithCurrency(sellingCurrencyCode),
						mobile: "cell",
					}),
					textColumn({
						field: "salesPriceUnit",
						headerName: i18n.t("sales_price_unit"),
					}),
				],
				tenantConfig.erp.configuratorEnabled &&
					booleanColumn({
						field: "isAwaitingConfiguration",
						headerName: i18n.t("awaiting_configuration"),
						width: 155,
					}),
				showProductionFields &&
					enumColumn({
						field: "salesPartType",
						headerName: i18n.t("type"),
						enumLabels: getSalesPartTypeLabels(),
						width: 75,
					}),
				showProductionFields &&
					textColumn({
						field: "shopOrderBatchCode",
						headerName: i18n.t("shop_order_batch"),
					}),
				textColumn({
					field: "capacityQuantity",
					headerName: i18n.t("capacity_quantity"),
					width: 125,
				}),
				canReadCostData &&
					moneyColumn({
						field: "unitCost",
						headerName: formatLabelWithCurrencyCode(i18n.t("unit_cost"), domesticCurrencyCode),
						width: 125,
						renderCell: renderMoneyCellWithCurrency(domesticCurrencyCode),
					}),
				canReadCostData &&
					moneyColumn({
						field: "totalCost",
						headerName: formatLabelWithCurrencyCode(i18n.t("total_cost"), domesticCurrencyCode),
						width: 135,
						renderCell: renderMoneyCellWithCurrency(domesticCurrencyCode),
					}),
				parentLineId == null &&
					textColumn({
						field: "vatCodeName",
						headerName: i18n.t("vat_code"),
					}),
				textColumn({
					field: "partUnit",
					headerName: i18n.t("warehouse_unit"),
					width: 105,
				}),
				integerColumn({
					field: "customerOrderOfferLineId",
					headerName: i18n.t("id"),
					width: 75,
				}),
			]}
			actionBarComponents={
				<>
					<AsyncButton
						icon={faTrash}
						tooltip={deleteDisabledReason ?? i18n.t("delete_selected")}
						disabled={deleteDisabledReason != null}
						onClick={deleteSelectedRows}
					/>
					<DocumentsOfObjectButton
						objectRef={nullableAavoObjectRef(
							"CUSTOMER_ORDER_OFFER_LINE",
							globalOnlySelectedRow?.customerOrderOfferLineId,
						)}
					/>
					<AavoTextField
						label={i18n.t("search")}
						onSubmit={async (value) => {
							await refreshData({ searchQuery: value });
						}}
					/>
					{parentLineId == null && !hideSummary && (
						<CustomerOrderOrOfferSummaryText objectType={"CUSTOMER_OFFER"} summary={offerSummary} />
					)}
				</>
			}
			actionBarMenuComponents={[
				<ImportCustomerOfferLinesFromTabularDataButton
					key={"importCustomerOfferLinesFromTabularDataButton"}
					customerOfferId={customerOrderOfferId}
					afterImport={refreshData}
				/>,
				tenantConfig.erp.customerOfferLineImportMethods.includes("VERTEX") && (
					<ImportFromVertexIndButton
						key={"importFromVertex"}
						submit={async (fileHandle) => {
							await VertexToCustomerOfferLineApi.importVertexFileToCustomerOfferLines({
								customerOfferId: customerOrderOfferId,
								fileHandle: fileHandle,
							});
							await refreshData();
						}}
					/>
				),
			]}
			rowContextMenuComponents={(params) => (
				<CustomerOrderOfferLinesDataGridRowContextMenu
					refreshData={refreshData}
					editable={props.editable !== false}
					{...params}
				/>
			)}
			form={{
				addRowEnabled: editEnabled,
				editEnabled: editEnabled,
				dialogSize: "lg",
				dialogTitle: i18n.t("offer_line"),
				agentContext: (row) => ({
					key: defineAgentUiContextKey("erp.sales.customerOfferLine.formDialog"),
					data: {
						customerOrderOfferId,
						customerOrderOfferLineId: row?.customerOrderOfferLineId ?? null,
						parentLineId: (row != null ? row?.parentLineId : parentLineId) ?? null,
					},
					pathSegments:
						row?.customerOrderOfferLineId ?
							[`${i18n.t("offer_line")} ${row.customerOrderOfferLineId}`]
						:	[i18n.t("offer_line")],
				}),
				component: ({ row, ...other }) => (
					<CustomerOrderOfferLineForm
						customerOrderOfferId={customerOrderOfferId}
						customerOrderOfferLineId={row?.customerOrderOfferLineId}
						parentLineId={(row != null ? row?.parentLineId : parentLineId) ?? undefined}
						disabled={!editEnabled}
						{...other}
					/>
				),
				customEditAction: ({ openFormForRow }) => {
					const rowToEdit = getOnlyMember(globalSelectedRows);
					if (rowToEdit == null) {
						return {
							type: "disabled",
							tooltip: i18n.t("select_a_single_row"),
						};
					}
					return {
						type: "enabled",
						action: () => {
							openFormForRow(rowToEdit);
						},
					};
				},
			}}
			getDetailPanelContent={(params) => <DetailPanel {...props} {...params} />}
			agentContext={{
				key: defineAgentUiContextKey("erp.sales.customerOfferLines.grid"),
				data: {
					customerOrderOfferId,
					parentLineId,
				},
				selectedRowPathSegment: (row) => `${i18n.t("offer_line")} ${row.customerOrderOfferLineId}`,
				selectedRowsPathSegment: (count) => i18n.t("customer_offer_lines_selected_count", { count }),
				rowFields: [
					"customerOrderOfferLineId",
					"customerOrderOfferId",
					"parentLineId",
					"lineNumber",
					"salesPartId",
					"salesPartNo",
					"configurationOrPartDescription",
					"salesQuantityAsSalesUnits",
					"unitPrice",
					"totalPrice",
				],
			}}
			{...dataGridProps}
		/>
	);

	async function deleteSelectedRows() {
		const confirmed = await showConfirmDialog();
		if (!confirmed) return;

		await CustomerOrderOfferLineEditApi.delete_({
			customerOrderOfferLineIds: globalSelectedRows.map((row) => row.customerOrderOfferLineId),
		});
		await refreshData();
	}

	function getDeleteDisabledReason(): string | undefined {
		if (!editEnabled) return i18n.t("offer_is_closed");
		if (globalSelectedRows.length === 0) return i18n.t("select_at_least_one_row");
		return undefined;
	}
};

interface DetailPanelContentProps
	extends
		CustomerOrderOfferLinesDataGridInnerProps,
		GridRowParams<CustomerOrderOfferLinesDataGridApi_CustomerOrderOfferLineDto> {}

const DetailPanel = ({
	row: parentRow,
	data,
	refreshData,
	currentParams,
	customerOrderOfferId,
	globalSelectedRows,
	setGlobalSelectedRows,
}: DetailPanelContentProps) => {
	if (parentRow.childLines.length === 0) return null;

	const childLinesMapped: CustomerOrderOfferLinesDataGridApi_CustomerOrderOfferLineDto[] = parentRow.childLines.map(
		(childLine) => ({
			...childLine,
			childLines: [],
		}),
	);

	const selectedRowsForThisPackage = globalSelectedRows.filter(
		(row) => row.parentLineId == parentRow.customerOrderOfferLineId,
	);

	return (
		<HorizontalBox
			sx={{
				margin: 2,
				marginLeft: 6,
			}}
		>
			<StaticDataGridModel
				rows={childLinesMapped}
				gridId={"D74A0E43F5A300F8_detail"}
				getRowId={(row) => row.customerOrderOfferLineId}
				customFunctionUiActionView={"CUSTOMER_OFFER_LINES"}
				selectedRows={selectedRowsForThisPackage}
				onSelectionChanged={setGlobalSelectedRows}
				render={({ dataGridProps, getApi, selectedRows, onlySelectedRow, setSelectedRows }) => (
					<CustomerOrderOfferLinesDataGridInner
						data={{
							...data,
							customerOrderOfferLines: childLinesMapped,
						}}
						currentParams={currentParams}
						refreshData={refreshData}
						globalSelectedRows={globalSelectedRows}
						setGlobalSelectedRows={setGlobalSelectedRows}
						selectedRows={selectedRows}
						onlySelectedRow={onlySelectedRow}
						setSelectedRows={setSelectedRows}
						hideActionBar
						customerOrderOfferId={customerOrderOfferId}
						parentLineId={parentRow.customerOrderOfferLineId}
						getApi={getApi}
						dataGridProps={{
							...dataGridProps,
							refreshData: refreshData,
						}}
					/>
				)}
			/>
		</HorizontalBox>
	);
};
